import { Outcome } from "@ethossoftworks/outcome"
import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { ExternalRoutes } from "@lib/Routes"
import { UserCacheModule } from "@service/cache/UserCacheModule"
import { AppBloc, AppState } from "@state/app/AppBloc"
import { CompanyBloc } from "@state/company/CompanyBloc"
import { UserBloc } from "@state/user/UserBloc"
import { UserPreferencesBloc } from "@state/UserPreferencesBloc"

export class AppViewModel extends BlocCoordinator<[AppState], AppState> {
    constructor(
        private appBloc: AppBloc,
        private userPreferencesBloc: UserPreferencesBloc,
        private userBloc: UserBloc,
        private companyBloc: CompanyBloc,
        private userCacheModule: UserCacheModule
    ) {
        super([appBloc])
    }

    protected transform = ([appState]: [AppState]) => appState

    getUser = () => this.userBloc.state.user

    getCompanyLogo = () => this.companyBloc.state.companyLogoUrl
    async onMounted() {
        const userOutcome = await this.userBloc.authenticateUser()
        if (userOutcome.isError()) {
            //window.location.replace(
            ExternalRoutes.SmartHubLogin(
                `${window.location.pathname === "/" ? "/" : window.location.pathname.substr(1)}${
                    window.location.search
                }`
            )
            //)
            return
        }

        await this.userCacheModule.fetchSessionCache(this.userBloc.state.user)
        this.companyBloc.companyIdChanged(userOutcome.value.companyId)
        this.companyBloc.companyNameChanged(userOutcome.value.companyName)

        // Company lookup data (dropdown/filter options) loads in the background. Screens consume it
        // reactively through their BlocCoordinators, so first render doesn't need to wait for it.
        const companyDataHydration = this.hydrateCompanyData(userOutcome.value.companyId)

        // First render only blocks on the user preferences that determine what the initial screen
        // fetches: the default filter preset and table column settings are read once on screen mount.
        await Promise.all([
            this.userPreferencesBloc.fetchWorkRequestsTableColumns(),
            this.userPreferencesBloc.fetchWorkOrdersTableColumns(),
            this.userPreferencesBloc.fetchUserSettings(),
            this.userPreferencesBloc.fetchFilterPresets(),
        ] as Promise<Outcome<unknown>>[])

        this.appBloc.hasHydratedChanged(true)

        await companyDataHydration
    }

    private hydrateCompanyData = async (companyId: number) => {
        await Promise.all([
            this.companyBloc.fetchUrgencies(),
            this.companyBloc.fetchServiceCodes(),
            this.companyBloc.fetchCbaCodes(),
            this.companyBloc.fetchServiceScheduleFilterOptions(),
            this.companyBloc.fetchSettings(),
            this.companyBloc.fetchContacts(),
            this.companyBloc.fetchMakes(),
            this.companyBloc.fetchModels(),
            this.companyBloc.fetchTypes(),
            this.companyBloc.fetchCategories(),
            this.companyBloc.fetchClasses(),
            this.companyBloc.fetchSubCompanies(),
            this.companyBloc.fetchGroups(),
            this.companyBloc.fetchDistricts(),
            this.companyBloc.fetchSubDistricts(),
            this.companyBloc.fetchUnits(),
            this.companyBloc.fetchAliases(),
            this.companyBloc.fetchSitesInUse(),
            this.companyBloc.fetchAllSites(),
            this.companyBloc.fetchCompanyLogo(),
            this.companyBloc.fetchVendors(companyId),
            this.companyBloc.fetchTimeZones(),
        ] as Promise<Outcome<unknown>>[])

        // Needs the sub-company ids fetched above
        let companyIds: number[] = [companyId]
        if (this.companyBloc.state.subCompanies.length > 0) {
            companyIds = this.companyBloc.state.subCompanies.map((c) => c.id)
        }
        await this.companyBloc.fetchServiceScheduleItemsInUse(companyIds)

        // Report cache validity back to the server only after all cached fetches have settled
        await this.userCacheModule.updateSessionCache(this.userBloc.state.user)
    }
}
